import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { shows } from '@/mocks/shows';
import { categories } from '@/mocks/categories';
import { getRelatedShows } from '@/utils/shows';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useFavorites } from '@/hooks/useFavorites';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScoreBadge from '@/components/feature/ScoreBadge';
import ShowCard from '@/components/feature/ShowCard';
import EmptyState from '@/components/feature/EmptyState';
import SectionHeading from '@/components/feature/SectionHeading';
import CastList from '@/pages/detail/components/CastList';
import EpisodeList from '@/pages/detail/components/EpisodeList';

export default function DetailPage() {
  const { id = '' } = useParams();
  const show = shows.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useDocumentMeta({
    title: show ? `${show.title}详细介绍与评分 - 高评分电视剧` : '剧集未收录 - 高评分电视剧',
    description: show
      ? `${show.title}（${show.year}，${show.region}）评分 ${show.score} 分。${show.intro}`
      : '该剧集信息暂未收录。',
  });

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!show) {
    return (
      <div className="px-4 md:px-6 py-16 md:py-24">
        <EmptyState
          icon="ri-file-unknow-line"
          title="该剧集信息暂未收录"
          description="你访问的剧集不存在或已下架，可以返回首页继续浏览高评分电视剧。"
          actionLabel="返回首页"
          onAction={() => {
            window.REACT_APP_NAVIGATE('/');
          }}
        />
      </div>
    );
  }

  const favorited = isFavorite(show.id);
  const related = getRelatedShows(shows, show, 6);
  const banner = show.heroImage || show.poster;
  const primaryCategory = categories.find((item) => item.slug === show.categories[0]);
  const categoryLink = primaryCategory ? `/category/${primaryCategory.slug}` : '/category/guochan';

  const handleFavorite = () => {
    const next = toggleFavorite(show.id);
    setToast(next ? '已加入收藏（本地演示）' : '已取消收藏');
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setToast('分享链接已复制到剪贴板');
      } else {
        setToast(url);
      }
    } catch {
      setToast(url);
    }
  };

  const infoItems = [
    { label: '类型', value: show.tags.join(' / ') },
    { label: '导演', value: show.director },
    { label: '编剧', value: show.writers },
    { label: '主演', value: show.cast.map((member) => member.name).join('、') },
    { label: '年份', value: `${show.year} 年` },
    { label: '集数', value: `共 ${show.episodes} 集` },
    { label: '国家/地区', value: show.region },
    { label: '首播时间', value: show.firstAir },
  ];

  return (
    <div>
      <section className="relative overflow-hidden bg-primary-950">
        <img
          src={banner}
          alt={`${show.title}剧照`}
          title={`${show.title} - 高评分电视剧详情`}
          className="absolute inset-0 w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/50"></div>

        <div className="relative px-4 md:px-6 pt-8 md:pt-12 pb-10 md:pb-14">
          <div className="[&_a]:text-background-200 [&_span]:text-background-200">
            <Breadcrumb items={[{ label: primaryCategory ? primaryCategory.name : '分类', to: categoryLink }, { label: show.title }]} />
          </div>

          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 md:w-56 shrink-0 mx-auto md:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-50/20">
                <img
                  src={show.poster}
                  alt={`${show.title}海报`}
                  title={`${show.title} - 剧集海报`}
                  className="w-full h-full object-top object-cover"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <ScoreBadge score={show.score} size="md" />
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-background-50/15 text-background-100 backdrop-blur">
                  {show.status}
                </span>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-background-50/15 text-background-100 backdrop-blur">
                  {show.region}
                </span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mt-4">
                {show.title}
              </h1>
              <p className="text-sm text-background-300 mt-1">{show.originalTitle}</p>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-background-100/90 max-w-2xl">
                {show.intro}
              </p>

              <div className="flex items-center gap-3 mt-6 flex-wrap">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                      : 'bg-background-50/15 text-background-50 hover:bg-background-50/25'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-line"></i>
                  分享链接
                </button>
                <Link
                  to={categoryLink}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{primaryCategory ? primaryCategory.name : '分类'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-6 py-10 md:py-14">
        <section>
          <SectionHeading title="剧集信息" subtitle="结构化资料一览" icon="ri-information-line" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {infoItems.map((item) => (
              <div key={item.label} className="p-4 rounded-lg bg-background-50 border border-background-200">
                <span className="text-xs text-foreground-500">{item.label}</span>
                <p className="mt-1.5 text-sm font-medium text-foreground-900">{item.value}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <SectionHeading title="剧情简介" subtitle="故事背景与看点" icon="ri-book-open-line" />
          <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200 space-y-4">
            {show.synopsis.map((paragraph, index) => (
              <p key={index} className="text-sm md:text-base leading-loose text-foreground-700">
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <SectionHeading title="主要演员" subtitle="主演阵容与角色" icon="ri-user-star-line" />
          <CastList cast={show.cast} />
        </section>

        <section className="mt-12">
          <SectionHeading
            title="剧集列表"
            subtitle="点击章节查看剧情说明（仅信息展示，不含播放功能）"
            icon="ri-list-ordered"
          />
          <EpisodeList episodeCount={show.episodes} showTitle={show.title} />
        </section>

        {related.length > 0 && (
          <section className="mt-12">
            <SectionHeading title="相关推荐" subtitle="同类型或评分接近的剧集" icon="ri-magic-line" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
              {related.map((item) => (
                <ShowCard key={item.id} show={item} />
              ))}
            </div>
          </section>
        )}

        <p className="mt-12 text-xs text-foreground-400 text-center">
          本站所有剧集资料均为演示示例，不提供在线播放与下载功能。
        </p>
      </div>

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-md bg-primary-950 text-background-50 text-sm shadow-lg animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}